import React from 'react'

import PropTypes from 'prop-types'
import { omit } from 'ramda'

import { Chip } from '@mui/material'
import { makeStyles } from '@mui/styles'

const displayName = 'ToggleChip'

const styles = theme => ({
  root: {
    minWidth: '200px',
    fontSize: '14px',
    fontFamily: theme.typography.boldFontFamily,
    justifyContent: 'flex-start',
    padding: '0 0.5rem',
  },
  activeFilterChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.primary,
    '&:hover, &:focus, &:active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.text.primary,
    },
  },
  filterChip: {
    backgroundColor: theme.palette.primary.active,
    color: theme.palette.primary.main,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.text.primary,
    },
  },
  label: {
    flex: '1 1 auto',
  },
  deleteIcon: {},
})

const useStyles = makeStyles(styles, { name: displayName })

const classOmitter = omit(['activeFilterChip', 'filterChip'])
const propsOmitter = omit(['classes', 'active'])

/**
 * A chip-like button that changes visual state to indicate selected-ness
 * @component
 * @example
 * import Harvest from '~/src/UI/Icons/Batch'
 * {() => {
 *   const [active, setActive] = React.useState(null)
 *   const [counter, setCounter] = React.useState(0)
 *   const firstRef = React.useRef(null)
 *   const secondRef = React.useRef(null)
 *   React.useEffect(() => {
 *     setTimeout(() => {
 *       setActive(firstRef.current)
 *     }, 100)
 *   }, [])
 *
 *   return (
 *     <>
 *       <ToggleChip
 *         active={active && active === firstRef.current}
 *         icon={<Harvest color="inherit" />}
 *         label="First"
 *         ref={firstRef}
 *         onClick={() => { setActive(firstRef.current); setCounter(prev => prev + 1) }}
 *       />
 *       <span>{'\u00a0'}</span>
 *       <ToggleChip
 *         active={active && active === secondRef.current}
 *         label="Second"
 *         ref={secondRef}
 *         variant="outlined"
 *         onClick={() => { setActive(secondRef.current); setCounter(prev => prev + 1) }}
 *         onDelete={() => alert('deleted!')}
 *       />
 *       <span>{'\u00a0'}Click count: {counter}, Active: {active ? active.textContent.trim() : 'n/a'}</span>
 *     </>
 *   )
 * }}
 *
 *
 *
 */
const ToggleChip = React.forwardRef((props, ref) => {
  const classes = useStyles(props)
  const { active, label } = props

  return (
    <Chip
      ref={ref}
      aria-current={active}
      aria-label={label}
      classes={{
        ...classOmitter(classes),
        clickable: active ? classes.activeFilterChip : classes.filterChip,
      }}
      {...propsOmitter(props)}
    />
  )
})

ToggleChip.propTypes = {
  active: PropTypes.bool,
  classes: PropTypes.objectOf(PropTypes.string),
  label: PropTypes.string,
}
ToggleChip.defaultProps = {
  active: null,
  classes: {},
  label: '',
}

export default React.memo(ToggleChip)
